<template>
  <v-row>
    <v-col cols="3">
      <v-select :items="availableAggregationLevels"
                v-model="selectedTableAggregationLevel"
                density="compact"
                variant="outlined"
                label="Aggregation Level:"
      />
    </v-col>
    <v-spacer/>
  </v-row>

  <v-table density="comfortable" style="background: transparent" class="scrollable-horizontal">
    <thead :class="tableGradient">
    <tr>
      <th class="rounded-top-left-border"><b class="ml-10">KPI</b></th>
      <th class="text-center">Weight</th>
      <th class="text-center rounded-top-right-border">Weighted Score</th>
    </tr>
    </thead>

    <tbody>
    <template v-for="(object, i) in individualSectors" :key="object">

      <tr class="bg-white">
        <td colspan="3" class="text-center font-weight-black">
          Sector {{ ++i }}: {{ StringManipulation.methods.clearAndCapitalize(object) }}
        </td>
      </tr>

      <template v-for="(kpi, index) in filteredTableData[object].data" :key="kpi.id">
        <tr :class="index % 2 === 0 ? evenBackgroundRowColorCoding[selectedTableAggregationLevel] : oddBackgroundRowColorCoding[selectedTableAggregationLevel]">
          <td class="py-3 pl-5 kpi-text"><b>{{ ++index }}.  {{ StringManipulation.methods.clearAndCapitalize(kpi.name ) }}</b></td>
          <td class="text-center">{{ parseFloat(kpi.weight).toFixed(3) }}</td>
          <td class="text-center">{{ kpi.kpi_score ? (kpi.weight * kpi.kpi_score).toFixed(3) : '-' }}</td>
        </tr>
      </template>

      <tr class="bg-white">
        <td colspan="2">
          Final SPI Score of <b>{{ StringManipulation.methods.clearAndCapitalize(object) }}</b> Sector:
        </td>
        <td class="text-center font-weight-bold">
          {{ filteredTableData[object].sector_spi_score.toFixed(2) }}
        </td>
      </tr>

    </template>
    </tbody>
  </v-table>

</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import StringManipulation from "@/Mixins/StringManipulation";
import DownloadSpiAndSiiTablesButton from "@/Pages/Projects/subcomponents/DownloadSpiAndSiiTablesButton.vue";

const props = defineProps({
  kpiTableData: {
    type: Object,
    required: true,
  },
  buttonData: {
    type: Object,
    required: true,
  }
});

const selectedTableAggregationLevel = ref('');
const availableAggregationLevels = ref([]);

onMounted(() => {
  availableAggregationLevels.value = Object.keys(props.kpiTableData);
  selectedTableAggregationLevel.value = availableAggregationLevels.value[0];
});

const tableGradient = computed(() => {
  return tableGradientClassColorCoding[selectedTableAggregationLevel.value];
});

const filteredTableData = computed(() => {
  return props.kpiTableData[selectedTableAggregationLevel.value];
});

const individualSectors = computed(() => {
  if (filteredTableData.value) {
    return Object.keys(filteredTableData.value);
  }
  return [];
});

const tableGradientClassColorCoding = {
  'Building' : 'building-gradient',
  'PEB': 'peb-gradient',
  'PED': 'ped-gradient',
  'City': 'city-gradient',
  'Island': 'island-gradient',
};
const evenBackgroundRowColorCoding = {
  'Building' : 'building-even-row',
  'PEB': 'peb-even-row',
  'PED': 'ped-even-row',
  'City': 'city-even-row',
  'Island': 'island-even-row'
};
const oddBackgroundRowColorCoding = {
  'Building' : 'building-odd-row',
  'PEB': 'peb-odd-row',
  'PED': 'ped-odd-row',
  'City': 'city-odd-row',
  'Island': 'island-odd-row'
};


</script>

<style scoped>
/* Do NOT erase these gradients. They appear as unused, but they're being used dynamically by JS! */
.peb-gradient {
  background: linear-gradient(to left, #1f469c, #5487d0) !important;
  color: white;
}

.ped-gradient {
  background: linear-gradient(to left, #255e12, #4b913a) !important;
  color: white;
}

.city-gradient {
  background: linear-gradient(to left, #c87000, #edab00) !important;
  color: white;
}

.building-gradient {
  background: linear-gradient(to left, #9c1f1f, #d05454) !important;
  color: white;
}

.island-gradient {
  background: linear-gradient(to left, #601f9c, #9054d0) !important;
  color: white;
}

.building-even-row {
  background: #fff1f1;
}

.building-odd-row {
  background: #ffdede;
}

.ped-even-row {
  background: #f7fff1;
}

.ped-odd-row {
  background: #daf4d8;
}

.peb-even-row {
  background: #f4f8ff;
}

.peb-odd-row {
  background: #e4eaff;
}

.city-even-row {
  background: #fff8f2;
}

.city-odd-row {
  background: #ffeddd;
}

.island-even-row {
  background: #f6f2ff;
}

.island-odd-row {
  background: #e9ddff;
}

.rounded-top-left-border {
  border-top-left-radius: 30px;
  background-color: transparent !important;
}

.rounded-top-right-border {
  border-top-right-radius: 30px;
  border-color: #a6a6a6 !important;
}

.scrollable-horizontal {
  overflow-y: auto;
}

.kpi-text {
  max-lines: 2;
  max-width: 50rch;
}
</style>